import { type JSX, memo, useState } from "react";
import styled from "styled-components";
import { currentColors } from "../../shared/colors";
import PlusMinus from "../../shared/components/icons/PlusMinus";
import TaskIcon from "../../shared/components/icons/TaskIcon";
import { formatYMD, parseDate } from "../../shared/dateFns";
import t from "../../shared/translations";
import DownloadAnswers from "../myAnswers/DownloadAnswers";
import type { IFinishedPath } from "./MyHistory";
import { AccordionBody, AccordionContainer, AccordionHeader, AccordionHeading, SectionHeading } from "./stylings";

interface IProps {
    finishedPaths: IFinishedPath[];
}

const DatesHolder = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
`;

const TaskTable = styled.table`
    border: solid 1px ${currentColors.background};
    thead {
        background-color: ${currentColors.background};
    }
    
`;

const TaskA = styled.a`
    display: grid;
    grid-template-columns: 30px 1fr;
    align-items: center;
    gap: 0.3rem;
    
    svg {
        width: 30px;
        height: 30px;
    }
`;

function FinishedPaths({ finishedPaths }: IProps): JSX.Element {
    const [open, setOpen] = useState("");
    const toggle = (id: string) => {
        setOpen((currId) => (currId === id ? "" : id));
    };

    if (finishedPaths.length === 0) {
        return null;
    }

    return (
        <section>
            <SectionHeading>{t("player.my-history.heading-finished")}</SectionHeading>
            <div>
                {finishedPaths.map((path) => {
                    const miles = path.acceptedItems.reduce((acc, item) => acc + item.earnedMiles, 0);
                    return (
                        <AccordionContainer key={path.id}>
                            <AccordionHeader onClick={() => toggle(path.id)} $pointer={true}>
                                <AccordionHeading>{path.pathname}</AccordionHeading>
                                <div>{miles}</div>
                                <PlusMinus open={open === path.id} />
                            </AccordionHeader>
                            {open === path.id && (
                                <AccordionBody>
                                    <div>{t("player.my-history.label-miles-in-path", { miles })}</div>
                                    <DatesHolder>
                                        <div>{t("player.my-history.label-start-date", { date: path.startDate })}</div>
                                        <div>{t("player.my-history.label-end-date", { date: path.endDate })}</div>
                                    </DatesHolder>
                                    {path.acceptedItems.length > 0 && (
                                        <>
                                            <AccordionHeading>
                                                {t("player.my-history.heading-done-tasks")}
                                            </AccordionHeading>
                                            <TaskTable>
                                                <thead>
                                                    <tr>
                                                        <th>{t("player.my-history.table-tasks-heading-task")}</th>
                                                        <th>
                                                            {t("player.my-history.table-tasks-heading-finish-date")}
                                                        </th>
                                                        <th>{t("player.my-history.table-tasks-heading-miles")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {path.acceptedItems
                                                        .filter((i) => i.type !== "event")
                                                        .map((item) => (
                                                            <tr key={item.id}>
                                                                <td>
                                                                    <TaskA href={item.url}>
                                                                        <TaskIcon icon={item.icon} active={true} />
                                                                        {item.title}
                                                                    </TaskA>
                                                                </td>
                                                                <td>{formatYMD(parseDate(item.finishDatetime))}</td>
                                                                <td>{item.earnedMiles}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </TaskTable>
                                        </>
                                    )}
                                    <DownloadAnswers pathId={path.id} />
                                </AccordionBody>
                            )}
                        </AccordionContainer>
                    );
                })}
            </div>
        </section>
    );
}

export default memo(FinishedPaths);
